import { Suspense } from 'react';
import { Outlet } from 'react-router';

import { BottomTabBar } from '../components/AppNav';

import { RouteFallback } from './RouteFallback';

/**
 * The authenticated shell — the mockup's phone column.
 *
 * What changed and why:
 *
 *  - **No sidebar.** The previous shell grew a desktop left rail above `md`.
 *    That layout is not in the approved design at any breakpoint; the mockup is
 *    a single phone-width column with a floating tab bar, full stop.
 *  - **No sticky app header.** The mockup's screens open straight onto their
 *    own gradient panel, which carries the screen title. A second bar above it
 *    would push every hero down by 56px and appears nowhere in the reference.
 *    The theme and language controls moved to Profile, where settings belong.
 *  - **The width cap is on the COLUMN, not on `body`.** WalletFront pinned
 *    `max-width: 460px` to the body and was permanently a phone. Here the page
 *    background fills the viewport, the column is centred inside it, and on a
 *    wide display the column reads as a deliberate centred canvas rather than
 *    content stranded in the top-left corner.
 *
 * `main` carries no padding of its own: the gradient panels are full-bleed to
 * the column edges, so horizontal padding is owned by the screens.
 */
export function AppLayout() {
  return (
    <div className="min-h-dvh bg-[var(--bg-muted)]">
      <div
        className={[
          'mx-auto flex min-h-dvh w-full flex-col bg-[var(--bg-muted)]',
          'max-w-[var(--app-max-width)]',
          // A hairline either side on wide viewports so the column has an edge
          // to sit against instead of dissolving into the page.
          'md:shadow-[0_0_0_1px_var(--border)]'
        ].join(' ')}
      >
        <main
          id="main"
          // Clears the floating tab bar: its own height plus the gap it hovers
          // in, so the last row of any list stays reachable.
          className="flex-1 pb-[calc(var(--tabbar-height)+var(--safe-bottom)+40px)]"
        >
          {/* Suspense boundary for route-level code splitting. */}
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      <BottomTabBar />
    </div>
  );
}
